import type { BeforeSendHook, LogEvent, MeasurementEvent, TransportItem } from '@grafana/faro-web-sdk';
import { getWebInstrumentations, initializeFaro, LogLevel, TransportItemType } from '@grafana/faro-web-sdk';

import { MonitoringConfig } from './monitoring.config';

const WEB_VITALS_MEASUREMENT_TYPE = 'web-vitals';

let rumConsentGranted = false;

export function setRumConsent(granted: boolean): void {
    rumConsentGranted = granted;
}

/**
 * Errors, error-level logs and web vitals are always collected (legitimate
 * interest, cookieless). Behavioral RUM signals — events, custom measurements,
 * traces — are dropped until the user grants "analytics" cookie consent.
 */
export const consentFilter: BeforeSendHook = (item: TransportItem) => {
    if (rumConsentGranted) {
        return item;
    }

    switch (item.type) {
        case TransportItemType.EXCEPTION:
            return item;
        case TransportItemType.LOG:
            return (item.payload as LogEvent).level === LogLevel.ERROR ? item : null;
        case TransportItemType.MEASUREMENT:
            return (item.payload as MeasurementEvent).type === WEB_VITALS_MEASUREMENT_TYPE ? item : null;
        default:
            return null;
    }
};

/**
 * Must run before Angular bootstraps so bootstrap-phase errors are captured.
 * No-ops unless the environment enables monitoring AND provides a collector URL,
 * so local development and not-yet-provisioned environments send nothing.
 */
export function initializeMonitoring(config: MonitoringConfig | undefined): void {
    if (!config?.enabled || !config.url) {
        return;
    }

    initializeFaro({
        url: config.url,
        app: {
            name: config.appName,
            version: config.appVersion,
            environment: config.environment,
        },
        sessionTracking: {
            enabled: true,
            // Per-tab session only: no cookies or localStorage before consent.
            persistent: false,
            samplingRate: config.samplingRate,
        },
        instrumentations: [...getWebInstrumentations()],
        beforeSend: consentFilter,
    });
}
